import {
  Box,
  Button,
  Center,
  Divider,
  Group,
  Image,
  Loader,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import {
  IconArrowRight,
  IconExternalLink,
  IconPlugConnected,
  IconQuestionMark,
} from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useHistory, useParams } from 'react-router';
import { isEmpty, isNonNull, isNullish } from 'remeda';
import CompanyWebLogo from '../../../shared/components/Integrations/images/companyweb.png';
import { useLoadMonitoringData } from '../../../shared/hooks/use-load-monitoring-data';
import { ClientScore } from './components/ClientScore';
import { CreditLimit } from './components/CreditLimit';
import { MonitoringStatusActions } from './components/MonitoringStatusActions';
import { UpdateMonitoringDataButton } from './components/UpdateMonitoringDataButton';
import { WarningsOverview } from './components/WarningsOverview';

type DebtorInfoProps = {
  hasVatNumber: boolean;
};

export const Monitoring = ({ hasVatNumber }: DebtorInfoProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const history = useHistory();
  const company = useCompanyConfiguration();
  const { id: debtorId } = useParams<{ id: string }>();
  const { monitoring, isMonitoringLoading, monitoringError } = useLoadMonitoringData(debtorId);

  const companyWebCredentials = company.company_web_credentials;
  const isMonitoringEnabled =
    isNonNull(companyWebCredentials.login) && isNonNull(companyWebCredentials.password);

  if (!company.package?.can_use_monitoring) {
    return (
      <Panel mt="space7">
        <Stack align="center">
          <Text>{t(i18nKeys.ACCOUNT_LIMITATION)}</Text>
          <Button onClick={() => window.Intercom('show')} leftSection={<IconQuestionMark />}>
            {t(i18nKeys.MORE_INFO)}
          </Button>
        </Stack>
      </Panel>
    );
  }

  if (!isMonitoringEnabled) {
    return (
      <Panel mt="space7">
        <Stack align="center">
          <Text size="lg">{t(i18nKeys.DEBTOR.MONITORING.NOT_ENABLED)}</Text>
          <Button
            onClick={() => history.push('/settings/integrations?category=MONITORING')}
            leftSection={<IconPlugConnected stroke={1.5} />}
            rightSection={<IconArrowRight stroke={1.5} />}
          >
            {t(i18nKeys.DEBTOR.MONITORING.TO_SETTINGS)}
          </Button>
        </Stack>
      </Panel>
    );
  }

  if (monitoringError) {
    return (
      <Panel mt="space7">
        <Stack align="center">
          <Text size="lg">
            {monitoringError?.response?.data?.error_message ?? monitoringError.message}
          </Text>
        </Stack>
      </Panel>
    );
  }

  if (isMonitoringLoading) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  if (isNullish(monitoring)) {
    return (
      <Box
        mt="sm"
        bg="white"
        p="md"
        style={{ borderRadius: 4, border: `1px solid ${theme.colors.gray[2]}` }}
      >
        <Stack align="center">
          <Text size="lg">{t(i18nKeys.DEBTOR.MONITORING.NOT_EXISTING)}</Text>
          <Box w={150} mt="lg">
            <UpdateMonitoringDataButton hasVatNumber={hasVatNumber} debtorId={Number(debtorId)} />
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Panel mt={'space7'}>
      <Stack>
        <MonitoringStatusActions
          isMonitoringActive={monitoring.isMonitoringActive}
          updatedAt={monitoring.updatedAt}
          hasVatNumber={hasVatNumber}
          debtorId={debtorId}
        />
        <Center mt="lg">
          <Stack w={{ base: '100%', md: '95%', lg: '90%', xl: '80%' }}>
            <Group justify="space-between" align="flex-start" gap={20}>
              <Box w={{ base: '100%', md: '45%' }}>
                <ClientScore score={monitoring.score} />
                <Center>
                  <Button
                    variant="light"
                    component="a"
                    href={monitoring.reportUrl}
                    mt="xs"
                    target="_blank"
                    rightSection={<IconExternalLink size={16} stroke={1.5} />}
                  >
                    {t(i18nKeys.DEBTOR.MONITORING.FULL_REPORT)}
                  </Button>
                </Center>
              </Box>
              <Divider orientation="vertical" />
              <Box w={{ base: '100%', md: '45%' }}>
                <CreditLimit creditLimit={monitoring.creditLimit} />
              </Box>
            </Group>
            {!isEmpty(monitoring.warnings) && (
              <>
                <Divider w="100%" my="xl" />
                <Center>
                  <WarningsOverview
                    warnings={monitoring.warnings}
                    warningsURL={monitoring.warningsUrl}
                  />
                </Center>
              </>
            )}
            <Group w="100%" justify="right">
              <Image src={CompanyWebLogo} w={50} style={{ filter: 'grayscale(1)' }} />
            </Group>
          </Stack>
        </Center>
      </Stack>
    </Panel>
  );
};
